import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Music, Sparkles, Users, Star, Zap, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { getImageUrl } from '../utils/imageUrl';
import SEO from '../components/SEO';
import { seoConfig } from '../config/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback icon map if admin-defined features override the list
const ICON_MAP = { music: Music, sparkles: Sparkles, users: Users, star: Star, zap: Zap, heart: Heart };

const ExperiencePage = () => {
  const { t } = useTranslation();
  const [content, setContent] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/experience`).then((r) => setContent(r.data || {})).catch(() => setContent({}));
  }, []);

  // Default highlights (used when admin hasn't uploaded images yet)
  const defaultHighlights = [
    { icon: Music, title: t('experience.music'), description: t('experience.music_desc'), image: '/images/experience-music.jpg' },
    { icon: Sparkles, title: t('experience.decor'), description: t('experience.decor_desc'), image: '/images/experience-decor.jpg' },
    { icon: Users, title: t('experience.community'), description: t('experience.community_desc'), image: '/images/experience-community.jpg' },
  ];

  // Merge admin features with default highlights: if admin has uploaded an image for feature N,
  // use that. Otherwise fall back to the static image. Icons follow the admin-chosen icon if set.
  const highlights = (content?.features && Array.isArray(content.features) && content.features.length > 0)
    ? content.features.map((feat, i) => ({
        icon: ICON_MAP[feat.icon] || defaultHighlights[i]?.icon || Star,
        title: feat.title_fr || defaultHighlights[i]?.title || '',
        description: feat.description_fr || defaultHighlights[i]?.description || '',
        image: getImageUrl(feat.image_url) || defaultHighlights[i]?.image || '/images/experience-music.jpg',
      }))
    : defaultHighlights;

  const heroBg = getImageUrl(content?.hero_bg_url) || '/images/experience-bg.jpg';

  const genres = [
    'Reggaeton',
    'Dembow',
    'Baile Funk',
    'Hip Hop Latino',
    'Mambo',
    'Bachata',
    'Salsa',
    'Latin Trap'
  ];

  return (
    <div className="min-h-screen bg-[#060606]" data-testid="experience-page">
      <SEO {...seoConfig.experience} />
      {/* Hero Section */}
      <div className="relative h-[70vh] min-h-[500px] overflow-hidden">
        <img
          src={heroBg}
          alt="Experience Invasion Latina"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060606]/50 via-[#060606]/30 to-[#060606]" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-display text-5xl sm:text-7xl tracking-tight text-[#F5F0E8] mb-4">
              {t('experience.title')}
            </h1>
            <p className="font-serif italic text-xl sm:text-2xl text-[#C9A84C]">
              {t('experience.subtitle')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Intro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <p className="text-lg text-[#F5F0E8]/90 leading-relaxed">
            {t('experience.intro_1')} <span className="text-[#C9A84C]">{t('experience.intro_familia')}</span> {t('experience.intro_2')}
          </p>
        </motion.div>

        {/* Highlights */}
        <div className="space-y-24 mb-20">
          {highlights.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-16 ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className="lg:w-1/2">
                <div className="relative aspect-video rounded-2xl overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#060606]/60 to-transparent" />
                </div>
              </div>
              <div className="lg:w-1/2">
                <div className="w-14 h-14 rounded-full bg-[#C9A84C]/20 flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-[#C9A84C]" />
                </div>
                <h2 className="font-display text-3xl text-[#F5F0E8] mb-3">
                  {item.title}
                </h2>
                <p className="text-[#B9B2A8] leading-relaxed">
                  {item.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Genres */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="font-display text-3xl text-[#F5F0E8] mb-8">
            {t('experience.sounds_title')}
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {genres.map((genre) => (
              <span
                key={genre}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-[#F5F0E8] hover:border-[#C9A84C]/30 transition-colors"
              >
                {genre}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20"
        >
          {[
            { value: '16', label: t('experience.stat_years') },
            { value: '7000+', label: t('experience.stat_people') },
            { value: '8h', label: t('experience.stat_hours') },
            { value: '7', label: t('experience.stat_djs') }
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-display text-4xl sm:text-5xl text-[#C9A84C] mb-1">
                {stat.value}
              </div>
              <div className="text-xs uppercase tracking-widest text-[#B9B2A8]">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* CTA */}
        <div className="text-center">
          <h2 className="font-display text-2xl text-[#F5F0E8] mb-4">
            {t('experience.cta_title')}
          </h2>
          <p className="text-[#B9B2A8] mb-6">
            {t('experience.cta_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="/#tickets"
            >
              <Button
                size="lg"
                className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A] font-display uppercase tracking-wider gold-glow btn-active"
                data-testid="experience-tickets-cta"
              >
                {t('experience.cta_tickets')}
              </Button>
            </a>
            <Link to="/booking">
              <Button
                size="lg"
                variant="outline"
                className="border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/10 font-display uppercase tracking-wider"
                data-testid="experience-vip-cta"
              >
                {t('experience.cta_vip')}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom padding for mobile bar */}
      <div className="h-20 lg:h-0" />
    </div>
  );
};

export default ExperiencePage;
